import {useCallback} from 'react'

import {COMMAND_TYPES} from '@github-ui/copilot-shared-ui/ahp/command-types'
import {useOptionalEstablishedAhpSessionViewState} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session-view'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useCLIModeSync} from '../../hooks/use-cli-mode-sync'
import {useCreateCommand} from '../../hooks/useCreateCommand'
import {useEstablishedApprovalModeMutation} from '../../hooks/use-established-approval-mode-mutation'
import {CLIModePicker, type CLIAgentMode} from './CLIModePicker'

interface CLIModeToolbarSlotProps {
  sessionId: string
  taskId?: string
}

export function CLIModeToolbarSlot({sessionId, taskId}: CLIModeToolbarSlotProps) {
  const {agentMode, isPending, handleModeChange, handleModeChangeError} = useCLIModeSync({sessionId, taskId})
  const {approvalMode, effectiveApprovalMode, availableApprovalModes, approvalModeMutable} =
    useOptionalEstablishedAhpSessionViewState()
  const independentModeControlsEnabled = isFeatureEnabled('copilot_mission_control_independent_mode_controls')
  const approvalMutation = useEstablishedApprovalModeMutation()

  const {createCommand} = useCreateCommand({
    onError: (_error, snapshot) => {
      const previousMode = (snapshot as {previousMode?: CLIAgentMode})?.previousMode
      if (previousMode) {
        handleModeChangeError(previousMode)
      }
    },
  })

  const onModeChange = useCallback(
    (mode: CLIAgentMode) => {
      const {previousMode} = handleModeChange(mode)
      createCommand({
        sessionId,
        content: JSON.stringify({mode}),
        type: COMMAND_TYPES.MODE_SWITCH,
        snapshot: {previousMode},
      })
    },
    [sessionId, createCommand, handleModeChange],
  )

  return (
    <CLIModePicker
      selectedMode={agentMode}
      onModeChange={onModeChange}
      disabled={isPending || approvalMutation.isPending}
      selectedApprovalMode={independentModeControlsEnabled ? approvalMode : undefined}
      effectiveApprovalMode={independentModeControlsEnabled ? effectiveApprovalMode : undefined}
      availableApprovalModes={independentModeControlsEnabled ? availableApprovalModes : undefined}
      onApprovalModeChange={independentModeControlsEnabled ? approvalMutation.onApprovalModeChange : undefined}
      approvalModeMutable={approvalModeMutable}
      approvalModePending={approvalMutation.isPending}
      approvalModeError={approvalMutation.error}
    />
  )
}
